import { ApiError, apiErrorCode } from "@/lib/api";
import type { AccountProgramCapacity } from "@/lib/brokerAccounts";
import {
  BOND_FINAL_STEP_KEY,
  type BondSubmission,
  type WonBondsRequested,
} from "@/lib/brokerBonds";
import { REQUEST_REFUSED, VALIDATION_REASON_COPY } from "@/lib/errors";
import {
  anchorServerError,
  type AnchoredServerError,
} from "@/lib/serverFieldErrors";
import { formatBondCurrency } from "@/lib/bondValueDisplay";
import { isEndedStatus } from "@/lib/bondStateModel";

// BRK-S184 — the won-bid screen's rules, as the portal needs them to EXPLAIN
// what the server decides.
//
// ── ⚠️ MIRRORS, NOT AUTHORITIES ──────────────────────────────────────────────
//
// Nothing here gates the write. The price-change note and the spread-letter
// notice are INFORMATION (neither blocks Submit), and the capacity preview is
// a preview: PMS runs `checkCapacity` on the final price and answers
// `status` + `capacity` — `underwriter_review` when over a limit,
// never a refusal — and the banner after the submit renders from THAT, never
// from the preview (NN #7). The same parity arrangement as `bidResult.ts`:
// the rules are restated, pinned by `test/lib/wonBid.test.ts` against the
// worked figures, and the server wins when the two disagree.

/** The screen's one threshold, as a PERCENTAGE — both rules use it. Named for
 *  the same reason `THRESHOLD_PERCENT` is: it appears in copy too. */
export const WON_BID_THRESHOLD_PERCENT = 10;

/** The estimate the final price is compared with: the order's own
 *  `estimatedContractPrice`, or null. NOT `ourBidFromOrder` — that falls back
 *  to `bondAmount`, which is the penalty, not a contract price. A formatted
 *  string is refused rather than parsed (D-10, both directions). */
export function estimateFromOrder(order: BondSubmission): number | null {
  const value = order.values?.[BOND_FINAL_STEP_KEY]?.estimatedContractPrice;
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

/** How far the final price moved from the estimate, or null when either is
 *  missing. `percent` is rounded to one place for DISPLAY; the note's rule
 *  below compares unrounded figures. */
export function priceChange(
  estimate: number | null,
  final: number | undefined,
): { amount: number; percent: number } | null {
  if (estimate === null || final === undefined || !(final > 0)) return null;
  const amount = final - estimate;
  return {
    amount,
    percent: Math.round((amount / estimate) * 100 * 10) / 10,
  };
}

/** "+$12,000 (+2.7 %) from your estimate" — the live difference line. */
export function formatPriceChange(change: {
  amount: number;
  percent: number;
}): string {
  if (change.amount === 0) return "The same as your estimate";
  const sign = change.amount > 0 ? "+" : "-";
  return `${sign}${formatBondCurrency(Math.abs(change.amount))} (${sign}${String(Math.abs(change.percent))} %) from your estimate`;
}

/**
 * Does the final price differ from the estimate by MORE than 10 %?
 *
 * Strictly — exactly 10 % shows no note. Cross-multiplied rather than divided
 * (the `isPastThreshold` fix): `|final − estimate| / estimate > 0.10` in
 * doubles can land either side of the line on a clean 10 %.
 */
export function priceChangeNeedsNote(
  estimate: number | null,
  final: number | undefined,
): boolean {
  if (estimate === null || final === undefined || !(final > 0)) return false;
  return (
    Math.abs(final - estimate) * 100 > estimate * WON_BID_THRESHOLD_PERCENT
  );
}

/**
 * Is a Bid Spread Letter asked for? When the next-closest bid is MORE than
 * 10 % above the final price — `next > final × 1.10`, strictly, so exactly
 * 10 % asks for none. Cross-multiplied for the same reason as above.
 */
export function spreadLetterRequired(
  final: number | undefined,
  nextClosest: number | undefined,
): boolean {
  if (final === undefined || !(final > 0)) return false;
  if (nextClosest === undefined || !(nextClosest > 0)) return false;
  return nextClosest * 100 > final * (100 + WON_BID_THRESHOLD_PERCENT);
}

export const SECOND_NEEDS_NEXT =
  "Enter the next-closest bid before the second-closest.";
export const SECOND_BELOW_NEXT =
  "The second-closest bid can’t be lower than the next-closest bid.";

/** The two optional bid figures' inline errors. Either, both or neither may be
 *  given; the second needs the first and must be at least it. */
export function bidFigureErrors(
  nextClosest: number | undefined,
  secondClosest: number | undefined,
): { nextClosestBidAmount?: string; secondClosestBidAmount?: string } {
  if (secondClosest === undefined) return {};
  if (nextClosest === undefined) {
    return { secondClosestBidAmount: SECOND_NEEDS_NEXT };
  }
  if (secondClosest < nextClosest) {
    return { secondClosestBidAmount: SECOND_BELOW_NEXT };
  }
  return {};
}

// ── Bonds requested (BRK-S327) ───────────────────────────────────────────────
//
// A Performance bond is ALWAYS issued on a won bid; a Payment bond is
// sometimes issued alongside it and does not change the charge (Surety
// Sprint 10 plan, rule 4). So the screen asks one question — include the
// Payment bond? — and the writer says `performance` or `both`, never
// `payment`. The READERS stay tolerant: a result recorded before the change
// can still carry `payment` (consumer-first: PMS-S500 / SUR-S154 narrow the
// vocabulary after this ships).

/** The one checkbox, as the wire's value. */
export function bondsRequestedFrom(includePayment: boolean): WonBondsRequested {
  return includePayment ? "both" : "performance";
}

/** Whether a recorded value asked for the Payment bond — prefilling the
 *  checkbox on a correction. An old `payment` counts: the correction then
 *  asks for both, since there is no payment-only request any more. An
 *  unmirrored value leaves it unticked rather than guessing. */
export function includesPayment(value: unknown): boolean {
  return value === "payment" || value === "both";
}

/** Which bonds a won bid asked for, in words, or null for a value this build
 *  has not mirrored — a caller omits rather than renders blank. */
export function bondsRequestedLabel(value: unknown): string | null {
  switch (value) {
    case "performance":
      return "Performance";
    case "payment":
      return "Payment";
    case "both":
      return "Performance and Payment";
    default:
      return null;
  }
}

// ── The capacity preview ─────────────────────────────────────────────────────

/**
 * How much of the account's utilization is THIS order's own hold — which PMS
 * excludes before it tests the final price (`utilizationExcludingOrder`,
 * capacity-ledger.md § 4). The account block publishes no per-order figure,
 * so it is restated from the order: an ended order holds nothing (a lost bid
 * or a withdrawal released it), a won result recorded with its final price holds that price
 * (the `replace`), and any other submitted bid holds its estimate (the
 * reservation made at submit).
 */
export function orderCapacityHold(order: BondSubmission): number {
  if (isEndedStatus(order.status)) return 0;
  const result = order.bidResult;
  if (
    result?.outcome === "won" &&
    typeof result.finalContractPrice === "number"
  ) {
    return result.finalContractPrice;
  }
  return estimateFromOrder(order) ?? 0;
}

export type CapacityPreview = "within" | "single" | "aggregate";

/**
 * The preview of PMS's `evaluateCapacity`: the single test first, then the
 * aggregate — the same order, so the notice names the limit the server will.
 *
 * NULL — say nothing — when the account holds no program (`capacity` null or
 * absent) or there is no price yet: PMS moves an order with no program to
 * `pp_requested`, and "within your limits" would name limits that do not
 * exist. A null limit inside the block is a program without that limit.
 */
export function wonCapacityPreview(
  capacity: AccountProgramCapacity | null | undefined,
  hold: number,
  final: number | undefined,
): CapacityPreview | null {
  if (!capacity || final === undefined || !(final > 0)) return null;
  if (capacity.singleLimit !== null && final > capacity.singleLimit) {
    return "single";
  }
  if (capacity.remaining !== null && final > capacity.remaining + hold) {
    return "aggregate";
  }
  return "within";
}

// ── Copy ─────────────────────────────────────────────────────────────────────

export const PRICE_CHANGE_NOTE =
  "Signal will be told about the change; it does not affect approval.";
export const WITHIN_LIMITS = "Within your program limits";

export function overLimitNotice(over: "single" | "aggregate"): string {
  const limit = over === "single" ? "single bond" : "aggregate program";
  return `This contract is above your ${limit} limit. You can still submit; Signal will review it.`;
}

/** Why "Save and send letter" did not save: the won bid is not complete yet
 *  (BRK-S331). The stepper's steps say what is missing (BRK-S421). */
export const SAVE_BEFORE_LETTER =
  "Finish the won bid so it can be saved, then send the letter.";

export const SPREAD_LETTER_NOTICE =
  "Bid Spread Letter required. The next-closest bid is more than 10 % above your final contract price, so Signal will want a letter explaining the spread. Upload it here and we’ll pass it on. You can still submit without it.";

export const AWARD_LETTER_REQUIRED =
  "Upload the award letter or contract to submit.";
/** The fixed line's note: the Performance bond is not a choice. */
export const PERFORMANCE_ALWAYS = "Always issued on a won bid.";
/** The Payment bond's note: it rides alongside, at no extra charge. */
export const PAYMENT_ALONGSIDE =
  "Issued alongside the Performance bond. It doesn’t change the premium.";
export const FINAL_PRICE_REQUIRED = "Enter the final contract price.";
export const CHECK_BID_FIGURES = "Check the bid figures above.";
export const UPLOAD_RUNNING = "Wait for the upload to finish.";

// ── The four steps (BRK-S421) ────────────────────────────────────────────────
//
// The wireframe's won-bid dialog. Each step waits only on what IT asks for,
// so Next says why it is waiting on the step the broker is looking at; the
// server's refusal of the finished body is put back on the step that asks
// for the field it names.

export const WON_BID_STEPS = [
  "Final price",
  "Bid results",
  "Confirm bond forms",
  "Award letter",
] as const;

const FIELD_STEP: Record<string, number> = {
  finalContractPrice: 0,
  nextClosestBidAmount: 1,
  secondClosestBidAmount: 1,
  bidResultsDocumentId: 1,
  spreadLetterDocumentId: 1,
  bondsRequested: 2,
  awardLetterDocumentId: 3,
};

/** The step a won-body field is asked on, or null for one no step asks. */
export function wonStepOfField(field: string): number | null {
  return Object.hasOwn(FIELD_STEP, field) ? FIELD_STEP[field] : null;
}

/** Why a step's Next (or the last step's Finish) waits. */
export function wonStepBlockers(
  step: number,
  facts: {
    finalPrice: number | undefined;
    figureErrors: Record<string, string>;
    awardLetter: boolean;
    uploading: boolean;
  },
): string[] {
  const reasons: string[] = [];
  if (step === 0 && !(facts.finalPrice !== undefined && facts.finalPrice > 0)) {
    reasons.push(FINAL_PRICE_REQUIRED);
  }
  if (step === 1 && Object.keys(facts.figureErrors).length > 0) {
    reasons.push(CHECK_BID_FIGURES);
  }
  if (step === 3 && !facts.awardLetter) reasons.push(AWARD_LETTER_REQUIRED);
  if (facts.uploading) reasons.push(UPLOAD_RUNNING);
  return reasons;
}

/** After the submit, on the detail page (the `wonSubmitted` banner). */
/** A 2xx whose `status` is neither — the save is real, the stage is not ours
 *  to state. */
export const WON_RECORDED = "Your won bid is recorded.";

/**
 * The banner's sentence: the STAGE from the response's v2 `status`, and
 * the bonds it names (BRK-S327) — "Your Performance bond request…" or "Your
 * Performance and Payment bond request…". A bonds value this build has not
 * mirrored names no bond ("Your bond request…") rather than guess.
 */
export function wonSubmittedMessage(
  status: string | null,
  bondsRequested: unknown,
): string {
  if (status !== "underwriter_review" && status !== "pp_requested") {
    return WON_RECORDED;
  }
  const bonds = bondsRequestedLabel(bondsRequested);
  const request = `Your ${bonds === null ? "" : `${bonds} `}bond request is with Signal`;
  return status === "underwriter_review"
    ? `${request} — it’s above your limits, so Signal will review it.`
    : `${request}.`;
}

// ── The refusals ─────────────────────────────────────────────────────────────

/** The won body's fields, as a refusal's `details.field` names them. */
export const WON_BID_FIELDS = [
  "finalContractPrice",
  "nextClosestBidAmount",
  "secondClosestBidAmount",
  "bidResultsDocumentId",
  "spreadLetterDocumentId",
  "awardLetterDocumentId",
  "bondsRequested",
] as const;

export const DOCUMENT_NOT_FOUND =
  "We couldn’t find that file on this request. Please upload it again.";

/**
 * A failed won submit, in the PORTAL's words where PMS's are not the
 * broker's (BRK-S279). Read as plain strings: the codes are not in the pinned
 * contract yet (PMS-S634), and `ServerErrorCode` is asserted equal to it, so
 * they are recognised here the way `documentRejection` reads
 * `DOCUMENT_REJECTED`: an unrecognised code still degrades to generic.
 *
 *   • `BID_RESULT_FIELD_REQUIRED` — PMS's sentence names the wire key, so the
 *     field gets "This is required." instead.
 *   • a 404 naming a document field — the id is not a live document of that
 *     slot's type on this order; the slot says so.
 *   • `BID_RESULT_FIELD_NOT_APPLICABLE` — a body this screen never builds.
 *   • `BID_RESULT_LOCKED`, `ORDER_CLOSED` — PMS's own broker-safe sentences,
 *     unchanged (`anchorServerError`).
 *   • `VALIDATION_FAILED` — per field, in the portal's words (anchorServerError).
 *
 * PMS-S568 retired `BID_RESULT_V2_NOT_ENABLED` and `BID_RESULT_REOPEN_NEEDS_V2`
 * with the bid-result v2 switch: PMS can no longer send either, so neither is
 * read here (BRK-S336).
 */
export function wonBidRefusal(err: unknown): AnchoredServerError {
  const code = apiErrorCode(err);
  const field = refusalField(err);
  const known = (WON_BID_FIELDS as readonly string[]).includes(field ?? "");
  if (code === "BID_RESULT_FIELD_REQUIRED" && field !== null && known) {
    return {
      byField: { [field]: VALIDATION_REASON_COPY.required },
      summary: "Please fix the highlighted fields and try again.",
    };
  }
  if (
    err instanceof ApiError &&
    err.status === 404 &&
    field !== null &&
    field.endsWith("DocumentId") &&
    known
  ) {
    return {
      byField: { [field]: DOCUMENT_NOT_FOUND },
      summary: DOCUMENT_NOT_FOUND,
    };
  }
  if (code === "BID_RESULT_FIELD_NOT_APPLICABLE") {
    return { byField: {}, summary: REQUEST_REFUSED };
  }
  return anchorServerError(err, WON_BID_FIELDS);
}

function refusalField(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return null;
  const field = (details as { field?: unknown }).field;
  return typeof field === "string" && field !== "" ? field : null;
}
